import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import TablePagination from '@material-ui/core/TablePagination';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@components/TextFieldCaseSetting';
import Button from '@material-ui/core/Button';
import styled from 'styled-components';
import AgGrid from '@components/AgGrid/AgGrid';
import * as globalActions from '../../actions/global';
import * as ownerActions from '../../actions/owner';
import NumberRenderer from '../../components/numberRenderer';
import SimpleLinkRenderer from '../../components/simpleLinkRenderer';
import { fetchingState } from '@constants';
import { propertyLookupModes } from '../../constants';
import selector from './selector';

const OwnerLookupModal = ({
  modalOpen,
  closeModal,
  fetchPreference,
  fetchFields,
  fields,
  lookupOwner,
  selectOwners,
  mode,
  userData,
  preference,
  data,
  state,
  recordCount,
  isIndependent,
  isIndependentOpen,
  independentSubmit,
  independentClose,
}) => {
  const [ownerId, setOwnerId] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [address, setAddress] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const agGridApi = useRef();
  const agGridColumnApi = useRef();

  useEffect(() => {
    fetchFields();
    fetchPreference({
      userId: userData.get('id'),
    });
  }, []);

  const closeProcedure = () => {
    setOwnerId('');
    setOwnerName('');
    setAddress('');
    setPage(1);
    setRowsPerPage(200);
    closeModal();
  };

  const performSearch = (page = 1, rows) => {
    setPage(page);
    if (rows) {
      setRowsPerPage(rows);
    }

    const searchOptions = {};
    if (ownerId) {
      searchOptions.ownerID = {
        operator: '=',
        value: ownerId,
      };
    }
    if (ownerName) {
      searchOptions['name-multi'] = {
        operator: 'match',
        value: ownerName,
      };
    }
    if (address) {
      searchOptions.address = {
        operator: 'match',
        value: address,
      };
    }

    lookupOwner({
      page,
      pageSize: rows || rowsPerPage,
      searchOptions,
    });
  };

  const buildColumnDefs = () => {
    if (!fields || !fields.get('data') || !data) {
      return [];
    }
    // const visibleColumns = preference.getIn([
    //   'data',
    //   'ownerSearch',
    //   'visibleColumns',
    // ]);

    let columnDefs =
      mode === propertyLookupModes.singleSelection
        ? []
        : [
            {
              field: 'select',
              headerName: '',
              checkboxSelection: true,
              width: 90,
            },
          ];

    columnDefs = columnDefs.concat(
      fields
        .get('data')
        .toJS()
        .map(field => ({
          headerName: field.displayName,
          field: field.id,
          cellRenderer: field.needFormatting
            ? NumberRenderer
            : undefined,
        })),
    );

    columnDefs = columnDefs.map(column => {
      if (column.field === 'ownerID') {
        return {
          ...column,
          width: 100,
          cellRenderer: SimpleLinkRenderer,
          cellRendererParams: {
            onClick: () => submitValues(),
          },
        };
      }
      return column;
    });
    return columnDefs;
  };

  const buildRowData = () =>
    data &&
    data.toJS() &&
    data.toJS().map((owner, index) => {
      const value = {};
      fields
        .get('data')
        .toJS()
        .forEach(field => {
          value[field.id] = owner[field.id];
        });
      value.seq = index + 1;
      return value;
    });

  const submitValues = () => {
    const owners = agGridApi.current.getSelectedRows().map(row => ({
      ownerId: row.ownerID,
      name: row.name,
      address: row.address,
    }));
    if (isIndependent) {
      independentSubmit(owners);
      independentClose();
    } else {
      selectOwners(owners);
      closeProcedure();
    }
  };

  const tabToNextCell = params => {
    const previousCell = params.previousCellPosition;
    let lastRowIndex = previousCell.rowIndex;
    let nextRowIndex = params.backwards ? lastRowIndex - 1 : lastRowIndex + 1;
    const renderedRowCount = agGridApi.current.getModel().getRowCount();
    if (nextRowIndex < 0) {
      nextRowIndex = 0;
    }
    if (nextRowIndex >= renderedRowCount) {
      nextRowIndex = renderedRowCount - 1;
    }
    const result = {
      rowIndex: nextRowIndex,
      column: previousCell.column,
      floating: previousCell.floating,
    };
    return result;
  };

  const agGridKeyDown = e => {
    e.preventDefault();
    const key = e.which || e.keyCode;
    if (key === 13) {
      const row = agGridApi.current.getFocusedCell();
      const rowNode = agGridApi.current.getDisplayedRowAtIndex(row.rowIndex);
      rowNode.setSelected(true);
      submitValues();
    }
  };

  return (
    <Dialog
      maxWidth={false}
      open={isIndependent ? isIndependentOpen : modalOpen}
      onClose={isIndependent ? independentClose : closeProcedure}
    >
      <DialogTitle id="form-dialog-title">
        Owner Lookup
        <CloseButton onClick={closeProcedure}>
          <CloseIcon />
        </CloseButton>
      </DialogTitle>
      <DialogContent
        style={{ paddingBottom: 0, maxWidth: 'none', width: '1100px' }}
      >
        <SearchForm>
          <TextField
            autoFocus
            margin="dense"
            id="ownerId"
            label="Owner ID"
            type="text"
            fullWidth
            onChange={e => setOwnerId(e.target.value)}
            style={{ marginRight: 10 }}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                performSearch();
              }
            }}
          />
          <TextField
            margin="dense"
            id="ownerName"
            label="Owner Name"
            type="text"
            fullWidth
            style={{ marginRight: 10 }}
            onChange={e => setOwnerName(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                performSearch();
              }
            }}
          />
          <TextField
            margin="dense"
            id="address"
            label="Address"
            type="text"
            fullWidth
            onChange={e => setAddress(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                performSearch();
              }
            }}
          />
          <Button
            disabled={ownerId === '' && ownerName === '' && address === ''}
            variant="contained"
            color="primary"
            style={{ height: 35, marginLeft: 10 }}
            onClick={() => performSearch()}
            onKeyDown={e => {
              console.log('keydown');
              if (e.key === 'Tab') {
                e.preventDefault();
                const firstCol = agGridColumnApi.current.getAllDisplayedColumns()[0];
                agGridApi.current.setFocusedCell(0, firstCol);
              }
            }}
          >
            Search
          </Button>
        </SearchForm>
        <TableContainer className="ag-theme-material">
          {state === fetchingState.FETCHING ? (
            <div style={{ textAlign: 'center' }}>
              <CircularProgress />
            </div>
          ) : (
            <AgGrid
              name="ownerLookup"
              columnDefs={buildColumnDefs()}
              rowData={buildRowData() || []}
              fetchPreference={fetchPreference}
              preference={preference}
              defaultColDef={{ sortable: true }}
              tabToNextCell={tabToNextCell}
              rowSelection={
                mode === propertyLookupModes.singleSelection
                  ? 'single'
                  : 'multiple'
              }
              rowMultiSelectWithClick={
                mode === propertyLookupModes.multiSelection
              }
              onGridReady={params => {
                // params.api.sizeColumnsToFit();
                params.columnApi.autoSizeAllColumns();
                agGridApi.current = params.api;
                agGridColumnApi.current = params.columnApi;

                params.api.gridPanel.eAllCellContainers.forEach(c =>
                  c.addEventListener('keydown', agGridKeyDown),
                );
              }}
            />
          )}
        </TableContainer>
        {recordCount && (
          <TablePagination
            backIconButtonProps={{
              'aria-label': 'Previous Page',
            }}
            component="div"
            count={recordCount}
            nextIconButtonProps={{
              'aria-label': 'Next Page',
            }}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
            onChangePage={(event, page) => performSearch(page + 1)}
            onChangeRowsPerPage={({ target }) =>
              performSearch(page + 1, target.value)
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeProcedure} color="primary">
          Cancel
        </Button>
        <Button onClick={submitValues} color="primary">
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

OwnerLookupModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  fetchFields: PropTypes.func.isRequired,
  fetchPreference: PropTypes.func.isRequired,
  fields: ImmutablePropTypes.map,
  lookupOwner: PropTypes.func.isRequired,
  mode: PropTypes.number,
  selectOwners: PropTypes.func.isRequired,
  state: PropTypes.string.isRequired,
  data: ImmutablePropTypes.list.isRequired,
  userData: ImmutablePropTypes.map,
  preference: ImmutablePropTypes.map,
  recordCount: PropTypes.number,
  page: PropTypes.number,
  isIndependent: PropTypes.any,
  isIndependentOpen: PropTypes.any,
  independentSubmit: PropTypes.func,
  independentClose: PropTypes.func,
};

OwnerLookupModal.defaultProps = {
  mode: 0,
};

const SearchForm = styled.div`
  display: flex;
  align-items: center;
`;

const CloseButton = styled(IconButton)`
  &&& {
    position: absolute;
    right: 10px;
    top: 10px;
    color: #888;
  }
`;

const TableContainer = styled.div`
  height: 450px;
  width: 100%;
  margin: 10px 4px 4px 4px;
`;

const mapDispatchToProps = dispatch => ({
  fetchPreference: bindActionCreators(globalActions.fetchPreference, dispatch),
  fetchFields: bindActionCreators(ownerActions.fetchFields, dispatch),
  lookupOwner: bindActionCreators(ownerActions.lookupOwner, dispatch),
  closeModal: bindActionCreators(ownerActions.closeOwnerLookupModal, dispatch),
  selectOwners: bindActionCreators(
    ownerActions.ownerLookupModalSelectRecords,
    dispatch,
  ),
});

export default connect(
  selector,
  mapDispatchToProps,
)(OwnerLookupModal);
